import React, { ChangeEvent, MouseEvent, FormEvent, FC } from "react";

import { FileRejection } from "react-dropzone";
import { toast } from "react-toastify";

import { convertToDropdownOption } from "~/utils";

import { FormDocumentFileType, useBoolean } from "~/hooks/";

import { Form, DropzoneFileFieldWithFile } from "~/components/atoms";
import {
  EditAndFormSubmitButton,
  LabelWithTextField,
  LabelWithMultipleDropdownField,
  FolderIconWithTitle,
  Thumbnail,
} from "~/components/molecules";

import { MultiValueType, OptionType } from "~/domains";

import {
  ACCEPT_IMAGE_FILE_TYPES,
  DOCUMENT_FILE_AVAILABLE_TYPE,
  DOCUMENT_FILE_MAX_BYTE_SIZE,
  FILE_MAX_BYTE_SIZE,
} from "~/constants/file";

type PropsType = {
  isSubmitting: boolean;
  defaultAccordionOpen: boolean;
  documentFile: FormDocumentFileType;
  selectableDocumentFileTagOptions: OptionType[];
  onChangeName: (newValue: string) => void;
  onChangeAttachFile: (newValue: File | undefined) => void;
  onChangeThumbnailImage: (newValue: File | undefined) => void;
  handleTagNameCreate: (newValue: string) => void;
  onChangeTags: (newValue: MultiValueType<OptionType>) => void;
  removeDocumentFile: () => void;
  saveDocumentFile: () => void;
  onChangeSetNotReadOnly: () => void;
  onChangeSetReadOnly: () => void;
};

export const DocumentFileForm: FC<PropsType> = ({
  isSubmitting,
  defaultAccordionOpen,
  documentFile,
  selectableDocumentFileTagOptions,
  removeDocumentFile,
  saveDocumentFile,
  onChangeName,
  onChangeAttachFile,
  onChangeThumbnailImage,
  handleTagNameCreate,
  onChangeTags,
  onChangeSetNotReadOnly,
  onChangeSetReadOnly,
}: PropsType) => {
  const accordionOpen = useBoolean(defaultAccordionOpen);

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    saveDocumentFile();
  };

  const handleNotReadonly = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    onChangeSetNotReadOnly();
  };

  const handleRemove = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    const isConfirm = confirm("本当に削除しますか？");
    if (!isConfirm) return;

    removeDocumentFile();
  };

  const handleAttachFileChange = (
    acceptedFile: File[],
    fileRejections: FileRejection[],
  ) => {
    if (fileRejections.length) {
      fileRejections.map(({ file, errors }) => {
        if (!errors[0]) return;
        toast.error(`${file.name} - ${errors[0].message}`);
      });
      return;
    }
    acceptedFile[0] && onChangeAttachFile(acceptedFile[0]);
  };

  const handleThumbnailImage = (
    acceptedFile: File[],
    fileRejections: FileRejection[],
  ) => {
    if (fileRejections.length) {
      fileRejections.map(({ file, errors }) => {
        if (!errors[0]) return;
        toast.error(`${file.name} - ${errors[0].message}`);
      });
      return;
    }
    acceptedFile[0] && onChangeThumbnailImage(acceptedFile[0]);
  };
  const dropdownTagValues = documentFile.documentFileTags.map((tag) =>
    convertToDropdownOption(tag),
  );

  const accordionToggle = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    accordionOpen.toggle();
  };

  const dropzoneClassName = `${
    documentFile.isReadOnly
      ? "bg-secondary-300"
      : "bg-primary-200 cursor-pointer"
  } rounded-3xl text-gray-500 flex justify-center items-center py-2 px-2`;

  return (
    <Form className="space-y-4" onSubmit={onSubmit}>
      <FolderIconWithTitle
        title={documentFile.name}
        onClick={accordionToggle}
      />
      {accordionOpen.isChecked && (
        <div className="space-y-6 ml-9">
          <LabelWithTextField
            labelText="名前"
            type="text"
            name="name"
            placeholder="名前"
            required
            value={documentFile.name}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              onChangeName(e.target.value)
            }
            disabled={documentFile.isReadOnly}
          />
          <DropzoneFileFieldWithFile
            readonly={documentFile.isReadOnly}
            label="ファイル"
            maxFileByte={DOCUMENT_FILE_MAX_BYTE_SIZE}
            acceptFileTypes={DOCUMENT_FILE_AVAILABLE_TYPE}
            onDropFiles={handleAttachFileChange}
            fileClassName={dropzoneClassName}
            onRemoveFile={() => onChangeAttachFile(undefined)}
            fileName={documentFile.attachFile.name}
            noFilesText="PowerPoint / PDF / Word / Excel / JPEG / PNG"
          />
          <DropzoneFileFieldWithFile
            readonly={documentFile.isReadOnly}
            label="サムネイル"
            maxFileByte={FILE_MAX_BYTE_SIZE}
            acceptFileTypes={ACCEPT_IMAGE_FILE_TYPES}
            onDropFiles={handleThumbnailImage}
            fileClassName={dropzoneClassName}
            onRemoveFile={() => onChangeThumbnailImage(undefined)}
            fileName={documentFile.thumbnailImage.name}
            noFilesText="JPEG / PNG"
          />
          <LabelWithMultipleDropdownField
            labelText="タグ"
            name="documentFileTag"
            options={selectableDocumentFileTagOptions}
            value={dropdownTagValues}
            onChange={onChangeTags}
            onCreateOption={handleTagNameCreate}
            creatable
            isDisabled={documentFile.isReadOnly}
          />
          {documentFile.thumbnailImage.url && (
            <div className="grid grid-cols-2 miniTablet:grid-cols-3 mt-8">
              <Thumbnail
                thumbnailImageUrl={documentFile.thumbnailImage.url}
                attachFile={documentFile.thumbnailImage}
                title={documentFile.name}
                tags={documentFile.documentFileTags.map((tag) => tag.name)}
                displayMode="preview"
              />
            </div>
          )}
          <EditAndFormSubmitButton
            isSubmitting={isSubmitting}
            isReadOnly={documentFile.isReadOnly}
            handleNotReadonly={handleNotReadonly}
            handleReadonly={onChangeSetReadOnly}
            handleRemove={handleRemove}
          />
        </div>
      )}
    </Form>
  );
};
